/* © CNES */
import { useContext, useEffect, useRef } from 'react';
import { authenticationAPI } from '../../api/authentication/AuthenticateAPI';
import { ConfigContext } from '../../context/ConfigContext';
import { AuthenticationResolution } from '../../models/AuthenticationResolution';
import { UserLoggedIn } from '../../models/UserLoggedIn';

/**
 * token Refresh Security Delay in MS.
 */
export const tokenRefreshSecurityDelayMS = 5000;

/**
 * Authentication Controller to initialize keycloak authentification.
 * @returns null.
 */
export const AuthenticationController = () => {
  const isAuthDisabled = process.env.NODE_ENV !== 'production';
  const context = useContext(ConfigContext);
  //creating a reference context object
  const contextRef = useRef(context);
  contextRef.current = context;

  /**
   * Controls authentication state initialization and login
   *
   */
  async function initialize() {
    return await authenticationAPI.init();
  }

  /**
   * initialize authentification when isAuthDisabled and userLoggedIn email changes.
   */
  useEffect(() => {
    if (!isAuthDisabled && !context.userLoggedIn.email) {
      initialize().then((authenticationStatus) => {
        switch (authenticationStatus.status) {
          case AuthenticationResolution.notAuthenticated:
            // not logged
            context.setUserLoggedIn({
              name: '',
              email: '',
              token: { value: '', expiresInMS: 0 },
            } as UserLoggedIn);
            break;
          case AuthenticationResolution.authenticated:
            // logged
            context.setUserLoggedIn(authenticationStatus.login);
            break;
          default:
            return;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthDisabled, context.userLoggedIn.email]);

  /**
   * Handle user login status when token expires.
   */
  useEffect(() => {
    if (!isAuthDisabled && context.userLoggedIn.email) {
      setTimeout(async () => {
        const context = contextRef.current;
        const newToken = await authenticationAPI.refreshSessionToken();
        if (newToken && context.userLoggedIn.token.value !== newToken.value) {
          context.setUserLoggedIn({ ...context.userLoggedIn, token: newToken });
        } else {
          authenticationAPI.logout();
        }
      }, context.userLoggedIn.token.expiresInMS - tokenRefreshSecurityDelayMS);
    }
  }, [isAuthDisabled, context]);

  return null;
};
